
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { User } from "../../types/auth";
import { updateUserProfile } from "@/services/auth/userProfileService";
import { updatePassword } from "@/services/auth/passwordService";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { ProfileImageSection } from "./profile/ProfileImageSection";
import { ProfileForm } from "./profile/ProfileForm";
import { Switch } from "@/components/ui/switch"; // Fixed import path
import { Label } from "@/components/ui/label";

interface ProfileTabProps {
  user: User | null;
}

const ProfileTab = ({ user }: ProfileTabProps) => {
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [profileImageUrl, setProfileImageUrl] = useState(user?.profileImage || "");
  const [sendConfirmationEmail, setSendConfirmationEmail] = useState(true);
  const { toast } = useToast();
  const { user: authUser } = useAuth();

  const validateFields = () => {
    if (!name.trim()) {
      toast({
        title: "Erro de validação",
        description: "O nome é obrigatório",
        variant: "destructive",
      });
      return false;
    }

    // Validate passwords if the user is trying to change them
    if (newPassword || confirmPassword) {
      if (newPassword !== confirmPassword) {
        toast({
          title: "Erro de validação",
          description: "As senhas não coincidem",
          variant: "destructive",
        });
        return false;
      }

      if (newPassword.length < 6) {
        toast({
          title: "Erro de validação",
          description: "A senha deve ter pelo menos 6 caracteres",
          variant: "destructive",
        });
        return false;
      }
    }
    
    return true;
  };

  const passwordsMatch = newPassword === confirmPassword || confirmPassword === "";
  const passwordMeetsRequirements = newPassword === "" || newPassword.length >= 6;
  const canSubmitPassword = (newPassword === "" && confirmPassword === "") || (newPassword !== "" && confirmPassword !== "" && passwordsMatch && passwordMeetsRequirements);

  const handleSaveChanges = async () => {
    if (!user || !authUser) {
      toast({
        title: "Erro",
        description: "Usuário não autenticado",
        variant: "destructive",
      });
      return;
    }
    
    if (!validateFields()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Update profile info
      const updates = {
        name,
        profileImage: profileImage || undefined
      };
      
      await updateUserProfile(user.id, updates);
      
      // Change password if provided
      if (newPassword && newPassword === confirmPassword) {
        await updatePassword(newPassword, sendConfirmationEmail);
      }
      
      toast({
        title: "Sucesso",
        description: "Perfil atualizado com sucesso",
      });
      
      // Reset password fields after successful update
      setNewPassword("");
      setConfirmPassword("");
      
    } catch (error: any) {
      console.error("Error updating profile:", error);
      
      let errorMessage = "Não foi possível atualizar o perfil";
      
      if (error.message && error.message.includes("password")) {
        errorMessage = "Erro ao alterar a senha. Verifique os requisitos de segurança.";
      }
      
      toast({
        title: "Erro",
        description: error.message || errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Informações do Perfil</CardTitle>
        <CardDescription>
          Atualize suas informações pessoais
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <ProfileImageSection 
          user={user}
          profileImage={profileImage}
          profileImageUrl={profileImageUrl}
          setProfileImage={setProfileImage}
          setProfileImageUrl={setProfileImageUrl}
        />

        <ProfileForm 
          user={user}
          name={name}
          email={email}
          newPassword={newPassword}
          confirmPassword={confirmPassword}
          passwordsMatch={passwordsMatch}
          passwordMeetsRequirements={passwordMeetsRequirements}
          isLoading={isLoading}
          setName={setName}
          setNewPassword={setNewPassword}
          setConfirmPassword={setConfirmPassword}
          onSaveChanges={handleSaveChanges}
          canSubmit={canSubmitPassword}
        >
          {/* Show email confirmation option when password fields are filled */}
          {(newPassword || confirmPassword) && (
            <div className="flex items-center space-x-2 mt-4">
              <Switch 
                id="send-confirmation-email"
                checked={sendConfirmationEmail}
                onCheckedChange={setSendConfirmationEmail}
              />
              <Label htmlFor="send-confirmation-email" className="text-sm text-muted-foreground">
                Enviar email de confirmação sobre a alteração de senha
              </Label>
            </div>
          )}
        </ProfileForm>
      </CardContent>
    </Card>
  );
};

export default ProfileTab;
